import { useEffect, useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { captureError } from "@/lib/sentry";
import type { NotificationType } from "@/types/notifications";
import {
  pushSupported,
  isIOS,
  isStandalone,
  urlBase64ToUint8Array,
} from "@/lib/push";

/**
 * Notification categories map a single user-facing toggle to one or more
 * notification_preferences `type` rows. Backend defaults: every type is ON
 * except `contact_api_created`, which is OFF. We mirror those defaults here so
 * the UI reflects the effective state before any explicit row exists.
 */
interface NotificationCategory {
  key: string;
  label: string;
  description: string;
  types: NotificationType[];
}

const CATEGORIES: NotificationCategory[] = [
  {
    key: "imports",
    label: "Imports",
    description:
      "When a contact import finishes, and when contacts are created via the API.",
    types: ["import_completed", "contact_api_created"],
  },
  {
    key: "email",
    label: "Email & campaigns",
    description:
      "When an email account needs reconnecting, and when a campaign completes, is paused, or fails.",
    types: [
      "email_reconnect",
      "campaign_completed",
      "campaign_paused",
      "campaign_failed",
    ],
  },
  {
    key: "product_update",
    label: "Product updates",
    description: "When there's something new in BuildIt to tell you about.",
    types: ["product_update"],
  },
];

/** Effective default for a type when no preference row exists. */
const DEFAULT_ENABLED: Record<NotificationType, boolean> = {
  import_completed: true,
  email_reconnect: true,
  campaign_completed: true,
  campaign_paused: true,
  campaign_failed: true,
  contact_api_created: false,
  product_update: true,
};

export function NotificationSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  // Per-type enabled state, seeded from defaults then overlaid with stored rows.
  const [prefs, setPrefs] = useState<Record<NotificationType, boolean>>(
    () => ({ ...DEFAULT_ENABLED }),
  );
  // Types currently being persisted (disables the relevant toggle).
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  // ---- Browser push (Phase 2) -------------------------------------------
  // A separate channel from the per-type in-app preferences above: this controls
  // whether THIS browser/device receives an OS-level Web Push. The in-app rows
  // (prefs) still gate whether a notification is emitted at all; push is the
  // delivery surface for the ones that are.
  const canPush = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from("notification_preferences")
          .select("type, enabled")
          .eq("user_id", user.id);
        if (error) throw error;
        if (cancelled) return;

        const next = { ...DEFAULT_ENABLED };
        for (const row of data ?? []) {
          if (row.type in next) {
            next[row.type as NotificationType] = row.enabled;
          }
        }
        setPrefs(next);
      } catch (err) {
        if (cancelled) return;
        captureError(err as Error, {
          feature: "notification-preferences-load",
        });
        toast({
          title: "Error",
          description: "Failed to load notification preferences",
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, toast]);

  // Reflect the actual push subscription on mount. Do NOT request permission
  // here — permission must only be requested from a user gesture.
  useEffect(() => {
    if (!canPush) return;
    let active = true;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (active) setPushEnabled(!!sub);
      } catch {
        /* best-effort reflection only */
      }
    })();
    return () => {
      active = false;
    };
  }, [canPush]);

  // A category is "on" if every one of its constituent types is on.
  const categoryEnabled = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const cat of CATEGORIES) {
      map[cat.key] = cat.types.every((t) => prefs[t]);
    }
    return map;
  }, [prefs]);

  const handleToggle = async (cat: NotificationCategory) => {
    if (!user) return;
    const nextEnabled = !categoryEnabled[cat.key];

    // Optimistic update.
    const prev = prefs;
    setPrefs((p) => {
      const next = { ...p };
      for (const t of cat.types) next[t] = nextEnabled;
      return next;
    });
    setSavingKeys((s) => new Set(s).add(cat.key));

    try {
      const rows = cat.types.map((t) => ({
        user_id: user.id,
        type: t,
        enabled: nextEnabled,
      }));
      const { error } = await supabase
        .from("notification_preferences")
        .upsert(rows, { onConflict: "user_id,type" });
      if (error) throw error;
    } catch (err) {
      // Roll back on failure.
      setPrefs(prev);
      captureError(err as Error, {
        feature: "notification-preferences-save",
      });
      toast({
        title: "Error",
        description: "Failed to update notification preferences",
        variant: "destructive",
      });
    } finally {
      setSavingKeys((s) => {
        const next = new Set(s);
        next.delete(cat.key);
        return next;
      });
    }
  };

  async function enablePush() {
    if (!user) return;
    setPushBusy(true);
    try {
      await navigator.serviceWorker.register("/sw.js");
      // requestPermission MUST run inside this user-gesture handler.
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast({
          title: "Notifications blocked",
          description:
            "Enable notifications in your browser settings to receive alerts.",
        });
        return;
      }
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as
        | string
        | undefined;
      if (!vapidKey) {
        throw new Error("VITE_VAPID_PUBLIC_KEY is not configured");
      }
      // pushManager.subscribe() requires an ACTIVE service worker. Await
      // `ready` (not the register() result) so a first-enable doesn't race the
      // SW still installing.
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const j = sub.toJSON();
      const { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: user.id,
          endpoint: j.endpoint!,
          p256dh: j.keys!.p256dh,
          auth: j.keys!.auth,
          user_agent: navigator.userAgent,
        },
        { onConflict: "endpoint" },
      );
      if (error) throw error;
      setPushEnabled(true);
      toast({ description: "Browser notifications enabled." });
    } catch (err) {
      captureError(err as Error, { feature: "push-enable" });
      // Brave ships with web push (Google FCM) disabled by default, so the
      // permission can be granted but subscribe() still fails. Point Brave users
      // at the exact setting instead of a generic error.
      const isBrave = !!(navigator as unknown as { brave?: unknown }).brave;
      toast({
        title: "Couldn't enable push notifications",
        description: isBrave
          ? 'Brave blocks web push by default. Open brave://settings/privacy, turn on "Use Google services for push messaging," relaunch Brave, then try again.'
          : "Your browser blocked the push subscription. Make sure notifications are allowed for this site, then try again.",
        variant: "destructive",
      });
    } finally {
      setPushBusy(false);
    }
  }

  async function disablePush() {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("endpoint", sub.endpoint);
        await sub.unsubscribe();
      }
      setPushEnabled(false);
    } catch (err) {
      captureError(err as Error, { feature: "push-disable" });
      toast({
        title: "Error",
        description: "Could not disable browser notifications",
        variant: "destructive",
      });
    } finally {
      setPushBusy(false);
    }
  }

  function handlePushToggle(next: boolean) {
    if (next) void enablePush();
    else void disablePush();
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Notifications</h2>
        <p className="text-muted-foreground">
          Choose which in-app notifications you receive.
        </p>
      </div>

      <div className="space-y-4">
        {CATEGORIES.map((cat) => (
          <div key={cat.key} className="flex items-center justify-between">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor={`notif-${cat.key}`}>{cat.label}</Label>
              <p className="text-sm text-muted-foreground">{cat.description}</p>
            </div>
            <Switch
              id={`notif-${cat.key}`}
              checked={!!categoryEnabled[cat.key]}
              disabled={loading || savingKeys.has(cat.key)}
              onCheckedChange={() => handleToggle(cat)}
            />
          </div>
        ))}
      </div>

      {/* Browser push: separate delivery channel, not a per-type preference. */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium">Browser push notifications</h3>
        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="space-y-0.5 pr-4">
            <Label htmlFor="push-notifications">Browser push notifications</Label>
            <p className="text-sm text-muted-foreground">
              {iosNeedsInstall
                ? "Add BuildIt to your Home Screen to enable notifications on iPhone/iPad."
                : canPush
                  ? "Get a desktop/browser alert even when BuildIt isn't the active tab."
                  : "Your browser does not support push notifications."}
            </p>
          </div>
          <Switch
            id="push-notifications"
            checked={pushEnabled}
            disabled={!canPush || iosNeedsInstall || pushBusy}
            onCheckedChange={handlePushToggle}
          />
        </div>
      </div>
    </div>
  );
}
